export type PopoverTrigger = 'hover' | 'click';
export interface PopoverOptions {
  trigger?: PopoverTrigger;
  openDelay?: number;
  closeDelay?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}
interface DotNetReference { invokeMethodAsync(method: string, value: boolean): Promise<unknown>; }

/** Interactive, anchored content. Native top-layer rendering keeps it outside clipped tables
 * without moving nodes owned by a framework renderer. */
export class Popover {
  private static instances = new Map<HTMLElement, Popover>();
  private static active: Popover | null = null;
  private static observing = false;
  private root!: HTMLElement;
  private trigger!: HTMLButtonElement;
  private panel!: HTMLElement;
  private options!: Required<PopoverOptions>;
  private dotnet?: DotNetReference;
  private listeners = new AbortController();
  private resize?: ResizeObserver;
  private openTimer?: ReturnType<typeof setTimeout>;
  private closeTimer?: ReturnType<typeof setTimeout>;
  private frame = 0;
  private visible = false;
  private pinned = false;
  private overTrigger = false;
  private overPanel = false;
  private suppressFocus = false;
  private disposed = false;

  constructor(root: HTMLElement, options: PopoverOptions = {}) {
    const existing = Popover.instances.get(root);
    if (existing) return existing;
    const trigger = root.querySelector<HTMLButtonElement>('[data-popover-trigger]');
    const panel = root.querySelector<HTMLElement>('.popover');
    if (!trigger || !panel) throw new Error('Popover requires a trigger and a .popover panel.');
    this.root = root;
    this.trigger = trigger;
    this.panel = panel;
    this.options = {
      trigger: options.trigger ?? (root.dataset.antumoTrigger === 'click' ? 'click' : 'hover'),
      openDelay: Math.max(0, options.openDelay ?? Number(root.dataset.antumoOpenDelay ?? 250)),
      closeDelay: Math.max(0, options.closeDelay ?? Number(root.dataset.antumoCloseDelay ?? 180)),
      placement: options.placement ?? 'bottom',
    };
    Popover.instances.set(root, this);
    Popover.observe();
    this.bind();
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');
    if (panel.id) this.trigger.setAttribute('aria-controls', panel.id);
  }

  static getInstance(root: HTMLElement): Popover | null { return this.instances.get(root) ?? null; }
  static getOrCreateInstance(root: HTMLElement, options: PopoverOptions = {}): Popover {
    return this.instances.get(root) ?? new Popover(root, options);
  }
  static initWithDotNetRef(id: string, dotnet: DotNetReference, options: PopoverOptions = {}): void {
    const root = document.getElementById(id);
    if (!root) return;
    const instance = this.getOrCreateInstance(root, options);
    instance.dotnet = dotnet;
  }
  static setVisibleById(id: string, visible: boolean): void {
    const root = document.getElementById(id);
    if (!root) return;
    const instance = this.getOrCreateInstance(root);
    if (visible) { instance.pinned = true; instance.show(); }
    else instance.hide();
  }
  static disposeById(id: string): void {
    // The renderer can remove the root before DisposeAsync arrives. The shared observer also
    // disposes disconnected roots, so neither timing leaks document listeners or references.
    const root = document.getElementById(id);
    if (root) this.instances.get(root)?.dispose();
    for (const instance of this.instances.values())
      if (!instance.root.isConnected) instance.dispose();
  }
  static autoInit(scope: ParentNode = document): void {
    scope.querySelectorAll<HTMLElement>('.antumo-popover[data-antumo-toggle="popover"], .antumo-popover[data-antumo-popover]').forEach(root => this.getOrCreateInstance(root));
    this.observe();
  }
  private static observe(): void {
    if (this.observing || typeof document === 'undefined') return;
    this.observing = true;
    document.addEventListener('pointerdown', event => {
      const active = this.active;
      if (active && event.target instanceof Node && !active.root.contains(event.target)) active.hide();
    }, true);
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || !this.active) return;
      event.preventDefault();
      event.stopPropagation();
      this.active.hide(true);
    }, true);
    const reposition = () => this.active?.schedulePosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    window.visualViewport?.addEventListener('resize', reposition);
    window.visualViewport?.addEventListener('scroll', reposition);
    new MutationObserver(records => {
      for (const instance of this.instances.values())
        if (!instance.root.isConnected) instance.dispose();
      for (const record of records) for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches('.antumo-popover[data-antumo-toggle="popover"], .antumo-popover[data-antumo-popover]')) this.getOrCreateInstance(node);
        node.querySelectorAll<HTMLElement>('.antumo-popover[data-antumo-toggle="popover"], .antumo-popover[data-antumo-popover]').forEach(root => this.getOrCreateInstance(root));
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  private bind(): void {
    const signal = this.listeners.signal;
    this.trigger.addEventListener('pointerenter', event => {
      if (event.pointerType === 'touch') return;
      this.overTrigger = true;
      clearTimeout(this.closeTimer);
      if (this.options.trigger === 'hover') {
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => this.show(), this.options.openDelay);
      }
    }, { signal });
    this.trigger.addEventListener('pointerleave', () => {
      this.overTrigger = false;
      clearTimeout(this.openTimer);
      this.scheduleClose();
    }, { signal });
    this.panel.addEventListener('pointerenter', () => {
      this.overPanel = true;
      clearTimeout(this.closeTimer);
    }, { signal });
    this.panel.addEventListener('pointerleave', () => {
      this.overPanel = false;
      this.scheduleClose();
    }, { signal });
    this.trigger.addEventListener('click', () => {
      if (this.visible && this.pinned) this.hide();
      else { this.pinned = true; this.show(); }
    }, { signal });
    this.root.addEventListener('focusin', event => {
      clearTimeout(this.closeTimer);
      if (!this.suppressFocus && event.target === this.trigger && this.options.trigger === 'hover'
          && this.trigger.matches(':focus-visible')) this.show();
    }, { signal });
    this.root.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (!this.root.contains(document.activeElement)) this.scheduleClose(true);
      });
    }, { signal });
    this.panel.querySelectorAll<HTMLElement>('[data-popover-close]').forEach(button => {
      button.addEventListener('click', () => this.hide(true), { signal });
    });
    this.panel.addEventListener('toggle', event => {
      if ((event as Event & { newState?: string }).newState === 'closed' && this.visible) this.hide();
    }, { signal });
  }

  show(): void {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    if (this.disposed || !this.root.isConnected || this.trigger.disabled || this.visible) return;
    Popover.active?.hide();
    Popover.active = this;
    this.visible = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.setAttribute('data-popover-open', 'true');
    if (typeof this.panel.showPopover === 'function') this.panel.showPopover();
    this.position();
    if (!this.visible) return;
    if (typeof ResizeObserver !== 'undefined') {
      this.resize = new ResizeObserver(() => this.schedulePosition());
      this.resize.observe(this.panel);
      this.resize.observe(this.trigger);
    }
    this.notify(true);
  }

  hide(restoreFocus = false): void {
    const restoreTrigger = restoreFocus && this.panel.contains(document.activeElement);
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.pinned = false;
    this.overPanel = false;
    if (!this.visible) return;
    this.visible = false;
    if (Popover.active === this) Popover.active = null;
    this.resize?.disconnect();
    this.resize = undefined;
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.panel.removeAttribute('data-popover-open');
    if (typeof this.panel.hidePopover === 'function' && this.panel.matches(':popover-open')) this.panel.hidePopover();
    this.trigger.setAttribute('aria-expanded', 'false');
    if (restoreTrigger && this.root.isConnected) {
      this.suppressFocus = true;
      this.trigger.focus({ preventScroll: true });
      queueMicrotask(() => { this.suppressFocus = false; });
    }
    this.notify(false);
  }

  private scheduleClose(force = false): void {
    clearTimeout(this.closeTimer);
    if (!force && (this.pinned || this.overTrigger || this.overPanel || this.root.contains(document.activeElement))) return;
    this.closeTimer = setTimeout(() => {
      if (!this.overTrigger && !this.overPanel && !this.root.contains(document.activeElement) && (force || !this.pinned)) this.hide();
    }, this.options.closeDelay);
  }
  private notify(visible: boolean): void {
    this.root.dispatchEvent(new CustomEvent(visible ? 'shown.antumo.popover' : 'hidden.antumo.popover', { bubbles: true }));
    void this.dotnet?.invokeMethodAsync('OnPopoverVisibilityChanged', visible).catch(() => {
      // A disconnected Blazor circuit cannot be notified; the local overlay is still cleaned up.
    });
  }
  private schedulePosition(): void {
    if (!this.visible || this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.position(); });
  }
  private position(): void {
    if (!this.visible) return;
    const viewport = window.visualViewport;
    const vw = viewport?.width ?? window.innerWidth;
    const vh = viewport?.height ?? window.innerHeight;
    const vx = viewport?.offsetLeft ?? 0;
    const vy = viewport?.offsetTop ?? 0;
    const css = getComputedStyle(this.panel);
    const margin = parseFloat(css.getPropertyValue('--popover-viewport-padding')) || 0;
    const gap = parseFloat(css.getPropertyValue('--popover-gap')) || 0;
    const anchor = this.trigger.getBoundingClientRect();
    if (anchor.bottom < vy || anchor.top > vy + vh || anchor.right < vx || anchor.left > vx + vw) {
      this.hide(); return;
    }
    this.panel.style.setProperty('--popover-available-width', `${Math.max(0, vw - 2 * margin)}px`);
    this.panel.style.setProperty('--popover-available-height', `${Math.max(0, vh - 2 * margin)}px`);
    const bounds = this.panel.getBoundingClientRect();
    const below = Math.max(0, vy + vh - margin - anchor.bottom - gap);
    const above = Math.max(0, anchor.top - vy - margin - gap);
    let placement = this.options.placement;
    if (placement === 'bottom' && below < bounds.height && above > below) placement = 'top';
    else if (placement === 'top' && above < bounds.height && below > above) placement = 'bottom';
    if (placement === 'right' && vx + vw - margin - anchor.right - gap < bounds.width) placement = 'left';
    if (placement === 'left' && anchor.left - vx - margin - gap < bounds.width) placement = 'bottom';
    let x = anchor.left;
    let y = anchor.bottom + gap;
    if (placement === 'top' || placement === 'bottom') {
      const available = placement === 'top' ? above : below;
      this.panel.style.setProperty('--popover-available-height', `${available}px`);
      const height = this.panel.getBoundingClientRect().height;
      y = placement === 'top' ? anchor.top - gap - height : anchor.bottom + gap;
    } else {
      x = placement === 'left' ? anchor.left - gap - bounds.width : anchor.right + gap;
      y = anchor.top;
    }
    const actual = this.panel.getBoundingClientRect();
    x = Math.max(vx + margin, Math.min(x, vx + vw - margin - actual.width));
    y = Math.max(vy + margin, Math.min(y, vy + vh - margin - actual.height));
    this.panel.style.left = `${x}px`;
    this.panel.style.top = `${y}px`;
    this.panel.dataset.popoverPlacement = placement;
  }
  dispose(): void {
    if (this.disposed) return;
    this.hide();
    this.disposed = true;
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.listeners.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.dotnet = undefined;
    Popover.instances.delete(this.root);
  }
}
